// components/admin/booking-management.tsx
import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { API_BASE_URL } from "@/lib/api"
import { Search, Filter, Download, Eye } from "lucide-react"

interface Booking {
  id: string
  type: 'single' | 'rajshree' | 'choice'
  number?: number
  numbers?: number[]
  amount: number
  qty?: number
  category?: string
  createdAt: string
  user: {
    userId: string
    name: string
    role: string
  }
  session: {
    name: string
    drawTime: string
    active: boolean
  }
}

interface BookingManagementProps {
  userId?: string // If provided, shows bookings for specific user
}

export function BookingManagement({ userId }: BookingManagementProps) {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    category: 'all',
    sessionId: '',
    search: ''
  })
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalCount: 0,
    totalPages: 0
  })
  const { toast } = useToast()

  const fetchBookings = async (page = 1) => {
    setLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      
      // Create URLSearchParams with proper filtering
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      })

      // Only add category to params if it's not 'all'
      if (filters.category && filters.category !== 'all') {
        params.append('category', filters.category)
      }

      if (filters.search) {
        params.append('search', filters.search)
      }

      if (filters.sessionId) {
        params.append('sessionId', filters.sessionId)
      }

      if (userId) {
        params.delete('page')
        params.delete('limit')
      }

      const url = userId 
        ? `${API_BASE_URL}/admin/users/${userId}/bookings?${params}`
        : `${API_BASE_URL}/admin/bookings?${params}`

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setBookings(data.bookings || [])
        if (!userId) {
          setPagination(prev => ({
            ...prev,
            page: data.pagination?.page || 1,
            totalCount: data.pagination?.totalCount || 0,
            totalPages: data.pagination?.totalPages || 0
          }))
        }
      } else {
        throw new Error('Failed to fetch bookings')
      }
    } catch (error) {
      console.error('Error fetching bookings:', error)
      toast({
        title: "Error",
        description: "Failed to load bookings",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBookings(1)
  }, [filters.category, userId])

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const handleSearch = () => {
    fetchBookings(1)
  }

  const getBookingTypeColor = (type: string) => {
    switch (type) {
      case 'single': return 'bg-blue-500'
      case 'rajshree': return 'bg-green-500'
      case 'choice': return 'bg-purple-500'
      default: return 'bg-gray-500'
    }
  }

  const getDisplayNumbers = (booking: Booking) => {
    if (booking.type === 'single' && booking.number !== undefined) {
      return booking.number.toString()
    }
    if (booking.type === 'rajshree' && booking.numbers) {
      return booking.numbers.join(', ')
    }
    if (booking.type === 'choice' && booking.numbers) {
      return booking.numbers.join(', ')
    }
    return '-'
  }

  const exportBookings = () => {
    // Simple CSV export implementation
    const headers = ['User ID', 'Name', 'Type', 'Numbers', 'Amount', 'Category', 'Session', 'Date']
    const csvData = bookings.map(booking => [
      booking.user.userId,
      booking.user.name,
      booking.type,
      getDisplayNumbers(booking),
      booking.amount,
      booking.category || '-',
      booking.session.name,
      new Date(booking.createdAt).toLocaleString()
    ])

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bookings-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)

    toast({
      title: "Export Successful",
      description: "Bookings data has been exported to CSV",
    })
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Booking Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={filters.category}
                onValueChange={(value) => handleFilterChange('category', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="rajshree">Rajshree</SelectItem>
                  <SelectItem value="choice">Choice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="search">Search User</Label>
              <Input
                id="search"
                placeholder="User ID or name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
              />
            </div>

            <div className="flex items-end">
              <Button onClick={handleSearch} className="w-full">
                <Search className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </div>

            <div className="flex items-end">
              <Button 
                onClick={exportBookings} 
                variant="outline" 
                className="w-full"
                disabled={bookings.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            {userId ? 'User Bookings' : 'All Bookings'} 
            <Badge variant="secondary" className="ml-2">
              {pagination.totalCount} total
            </Badge>
          </CardTitle>
          <CardDescription>
            {userId ? 'Booking history for specific user' : 'All bookings across all users and categories'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading bookings...</div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Numbers</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Session</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={`${booking.type}-${booking.id}`}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{booking.user.userId}</div>
                            <div className="text-sm text-muted-foreground">
                              {booking.user.name}
                            </div>
                            <Badge variant="outline" className="mt-1">
                              {booking.user.role}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getBookingTypeColor(booking.type)}>
                            {booking.type.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">
                          {getDisplayNumbers(booking)}
                        </TableCell>
                        <TableCell className="font-medium">
                          ₹{booking.amount}
                        </TableCell>
                        <TableCell>
                          {booking.category || '-'}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{booking.session.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {new Date(booking.session.drawTime).toLocaleTimeString()}
                            </div>
                            {booking.session.active && (
                              <Badge variant="secondary" className="mt-1">Active</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(booking.createdAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {!userId && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages} 
                    ({pagination.totalCount} total bookings)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => fetchBookings(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => fetchBookings(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {!loading && bookings.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No bookings found matching your filters.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}